<template>
  <div
    class="SidebarLinks"
    :class="{
      'SidebarLinks--auto-flow': this.isAutoFlowUser,
      'SidebarLinks--student': !isVolunteer,
    }"
  >
    <template v-if="$route.path.indexOf('/onboarding') !== -1"></template>

    <template v-else-if="authenticated">
      <!-- WP-04V-C (D3): the rendered prototype DOES carry a language row in
           the sidebar, as the fifth nav item. P3 had moved it into
           StudentAppHeader's utility area on the reading that the prototype
           had no sidebar language control; the rendered measurement shows
           otherwise, so for students it moves back here (rendered below,
           after نمایه, so the order matches). Volunteer keeps this one,
           unchanged. Same real LanguageSwitcher component in both places. -->
      <language-switcher v-if="isVolunteer" />
      <!-- P1: student label is خانه (Home), not the legacy داشبورد
           (Dashboard) -- same route, own label, volunteer copy untouched. -->
      <sidebar-link
        to="dashboard"
        :is-volunteer="isVolunteer"
        :text="
          isVolunteer
            ? $t('appSidebar.sidebarLinks.dashboard')
            : $t('appSidebar.sidebarLinks.studentHome')
        "
      >
        <house-icon class="icon" />
      </sidebar-link>

      <!-- P1: student desktop nav restores گزارش‌ها (Reports / Learning
           History) to the existing sessions/history route, using its own
           label so the frozen volunteer "sessionHistory" copy below is
           untouched. -->
      <sidebar-link
        v-if="!isVolunteer"
        to="sessions/history"
        :is-volunteer="isVolunteer"
        :text="$t('appSidebar.sidebarLinks.learningHistory')"
      >
        <archive-icon class="icon" />
      </sidebar-link>

      <sidebar-link
        v-if="!isVolunteer && !isMinimalHomeStudent"
        to="favorite-coaches"
        :is-volunteer="isVolunteer"
        :text="$t('appSidebar.sidebarLinks.favoriteCoaches')"
      >
        <heart-icon class="icon" />
      </sidebar-link>

      <sidebar-link
        v-if="isVolunteer"
        to="training"
        :is-volunteer="isVolunteer"
        :text="$t('appSidebar.sidebarLinks.training')"
      >
        <graduation-cap-icon class="icon" />
      </sidebar-link>

      <sidebar-link
        v-if="isVolunteer"
        to="calendar"
        :is-volunteer="isVolunteer"
        :text="$t('appSidebar.sidebarLinks.schedule')"
      >
        <calendar-icon class="icon" />
      </sidebar-link>

      <sidebar-link
        v-if="isVolunteer && isTutorSessionHistoryActive"
        to="sessions/history"
        :is-volunteer="isVolunteer"
        :text="$t('appSidebar.sidebarLinks.sessionHistory')"
      >
        <archive-icon class="icon" />
      </sidebar-link>

      <sidebar-link
        v-if="isAdmin"
        to="admin"
        :is-volunteer="isVolunteer"
        :text="$t('appSidebar.sidebarLinks.admin')"
      >
        <folder-icon class="icon" />
      </sidebar-link>

      <!-- P1: student label is نمایه (Profile), not the legacy حساب کاربری
           -- same route, own label, volunteer copy untouched. -->
      <sidebar-link
        to="profile"
        :is-volunteer="isVolunteer"
        :text="
          isVolunteer
            ? $t('appSidebar.sidebarLinks.profile')
            : $t('appSidebar.sidebarLinks.studentProfile')
        "
      >
        <portrait-icon class="icon" />
      </sidebar-link>

      <sidebar-link
        v-if="showDashboardRedesign"
        :text="$t('appSidebar.sidebarLinks.referFriend')"
        :onClick="openReferFriendModal"
      >
        <refer-friend-icon class="icon" />
      </sidebar-link>

      <!-- WP-04V-C (D3): student language row, in the prototype's position --
           after نمایه and before the logout row. Presentation only; the
           switcher's own behaviour is untouched. -->
      <language-switcher v-if="!isVolunteer" :nav-row="true" />

      <!-- P3 full-shell visual remediation (00_*.md §13): Logout as the
           rendered prototype's last nav row (`.nav-item--logout`), not a
           separate legacy footer block, matching `00_*.md §13.1`. Same real
           `logout` handler AppSidebar/index.vue already owns -- passed down
           as a prop rather than duplicating AuthService.logout here.
           Volunteer keeps the existing separate footer link untouched
           (AppSidebar/index.vue's `AppSidebar-final-link`, still gated
           `isVolunteer` there). -->
      <sidebar-link
        v-if="!isVolunteer"
        variant="danger"
        :text="$t('appSidebar.sidebarLinks.studentLogout')"
        :onClick="logout"
        data-testid="student-nav-logout"
      >
        <student-home-icon name="logout" :size="22" class="icon" />
      </sidebar-link>

      <!-- Batch 45 (doc 45, approved deviation from the prototype's
           `.sidebar-support`): students reach support through the native
           Synova button, so this row is for the shells without one. -->
      <div v-if="!mobileMode && isVolunteer" class="SidebarLinks-about">
        {{ $t('appSidebar.sidebarLinks.aboutBorna') }}
      </div>
      <sidebar-link
        v-if="isVolunteer || isAdmin"
        to="contact"
        :is-volunteer="isVolunteer"
        :text="$t('appSidebar.sidebarLinks.contactUs')"
      >
        <envelope-icon class="icon" />
      </sidebar-link>
      <!-- P3 full-shell visual remediation (00_*.md §13, PF-13): the
           rendered prototype's sidebar has no primary "About Borna" / Legal
           Policy presentation for the student nav. The `legal` route stays
           fully defined in router.js -- untouched, still reachable directly
           -- it's simply no longer rendered as a primary student sidebar
           item (approved: 00_*.md §13, "About / Legal" mapping). It was
           never shown to volunteers either (this block was previously
           `v-if="!isVolunteer"`), so removing it changes nothing for that
           role. -->
      <sidebar-link
        v-if="isVolunteer"
        to="https://chat.whatsapp.com/EI0pQosY9DHAmJD9c1RCI4"
        :text="$t('appSidebar.sidebarLinks.community')"
        :openNewTab="true"
      >
        <whatsapp-logo-icon class="icon-whatsapp-logo" />
      </sidebar-link>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SidebarLink from './SidebarLink.vue'
import ArchiveIcon from '@/assets/archive.svg'
import CalendarIcon from '@/assets/sidebar_icons/calendar.svg'
import EnvelopeIcon from '@/assets/sidebar_icons/envelope.svg'
import FolderIcon from '@/assets/sidebar_icons/folder.svg'
import GraduationCapIcon from '@/assets/sidebar_icons/graduation-cap.svg'
import HeartIcon from '@/assets/heart.svg'
import HouseIcon from '@/assets/sidebar_icons/house.svg'
import PortraitIcon from '@/assets/sidebar_icons/portrait.svg'
import ReferFriendIcon from '@/assets/sidebar_icons/refer-friend-icon.svg'
import ReferralSVG from '@/assets/dashboard_icons/student/referral.svg'
import WhatsappLogoIcon from '@/assets/whatsapp.svg'
import AnalyticsService from '@/services/AnalyticsService'
import { EVENTS } from '@/consts'
import LanguageSwitcher from './LanguageSwitcher.vue'
import StudentHomeIcon from '../../StudentHome/StudentHomeIcon.vue'

export default {
  components: {
    SidebarLink,
    ArchiveIcon,
    CalendarIcon,
    EnvelopeIcon,
    FolderIcon,
    GraduationCapIcon,
    HeartIcon,
    HouseIcon,
    PortraitIcon,
    ReferFriendIcon,
    WhatsappLogoIcon,
    LanguageSwitcher,
    StudentHomeIcon,
  },
  props: {
    authenticated: Boolean,
    isVolunteer: Boolean,
    isAdmin: Boolean,
    mobileMode: Boolean,
    // P3 full-shell visual remediation (00_*.md §13): the real logout
    // handler AppSidebar/index.vue already owns (AuthService.logout),
    // passed down so the student-only nav-embedded Logout row (matching
    // the rendered prototype's `.nav-item--logout`) can call it without
    // duplicating auth logic here.
    logout: {
      type: Function,
      default: null,
    },
  },
  computed: {
    ...mapGetters({
      isTutorSessionHistoryActive: 'featureFlags/isTutorSessionHistoryActive',
      isAutoFlowUser: 'user/isAutoFlowUser',
      showDashboardRedesign: 'user/showDashboardRedesign',
    }),
    // TASK-SF-11 (D-03 / D-05), narrowed by P1: گزارش‌ها (Learning History)
    // was restored to student nav in P1 — this gate now only hides
    // favorite-coaches (اعتبار is still not part of the approved P1 nav).
    // Student-only: this is false whenever isVolunteer or isAdmin is true, so
    // every volunteer/admin branch renders byte-identically to before.
    isMinimalHomeStudent() {
      return this.authenticated && !this.isVolunteer && !this.isAdmin
    },
  },
  methods: {
    openReferFriendModal() {
      AnalyticsService.captureEvent(EVENTS.STUDENT_CLICKED_REFER_A_FRIEND)
      let header
      let subcopy
      if (this.referralCopy === 'baseline') {
        header = this.$root.$t(
          'appSidebar.sidebarLinks.referral.baseline.header'
        )
        subcopy = this.$root.$t(
          'appSidebar.sidebarLinks.referral.baseline.subCopy'
        )
      } else if (this.referralCopy === 'small-gift-card') {
        header = this.$root.$t(
          'appSidebar.sidebarLinks.referral.smallGiftCard.header'
        )
        subcopy = this.$root.$t(
          'appSidebar.sidebarLinks.referral.smallGiftCard.subCopy'
        )
      } else if (this.referralCopy === 'emotional-appeal-struggling') {
        header = this.$root.$t(
          'appSidebar.sidebarLinks.referral.emotionalAppealStruggling.header'
        )
        subcopy = this.$root.$t(
          'appSidebar.sidebarLinks.referral.emotionalAppealStruggling.subCopy'
        )
      }
      this.$store.dispatch('app/modal/show', {
        component: 'ReferralModal',
        data: {
          svg: ReferralSVG,
          showAccept: false,
          header,
          subcopy,
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.SidebarLinks {
  @include flex-container(column);
  @include child-spacing(top, 16px);

  &-about {
    @include font-category('body');
    color: $c-secondary-grey;
    margin-top: 40px;
    text-align: left;
  }

  &--auto-flow {
    visibility: hidden;
  }

  // WP-04V-C (root cause R6): the rendered prototype's nav rows are 48px tall
  // on a 55px pitch -- a 7px gap, not the shared 16px. Over six rows the 9px
  // difference put the student nav 27px out by the fourth item. Volunteer
  // keeps the 16px spacing above.
  &--student {
    @include child-spacing(top, $borna-nav-row-gap);

    // Measured: 22px glyph, 11px from the label. The shared rule below is
    // 24px at 0.8em.
    .icon {
      margin-right: 11px;
      width: 22px;
      height: 22px;
    }

    // The prototype separates the logout row from the nav list with a rule and
    // a wider gap, and squares off that row's top corners against it
    // (measured radius `0 0 14px 14px`, 14px clear of the row above).
    // `::v-deep` because `.SidebarLink` belongs to the child component's
    // template and therefore does not carry this component's scope id.
    > [data-testid='student-nav-logout'] {
      border-top: 1px solid $borna-border;
      margin-top: $borna-nav-row-gap * 2;

      ::v-deep .SidebarLink {
        border-end-start-radius: $borna-radius-nav;
        border-end-end-radius: $borna-radius-nav;
        border-start-start-radius: 0;
        border-start-end-radius: 0;
      }
    }
  }
}

.icon {
  margin-right: 0.8em;
  width: 24px;
}

.icon-whatsapp-logo {
  margin-right: 0.8em;
  width: 20px;
  height: 20px;
}

html.is-rtl {
  .SidebarLinks--student .icon {
    margin-left: 11px;
    margin-right: unset;
  }

  .icon {
    margin-left: 0.8em;
    margin-right: unset;
  }

  .icon-whatsapp-logo {
    margin-left: 0.8em;
    margin-right: unset;
  }

  .SidebarLinks {
    &-about {
      text-align: right;
    }
  }
}
</style>
